import { useEffect, useRef } from 'react'
import * as echarts from 'echarts/core'
import { BarChart, FunnelChart, LineChart, PieChart } from 'echarts/charts'
import {
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  TooltipComponent,
} from 'echarts/components'
import { CanvasRenderer, SVGRenderer } from 'echarts/renderers'
import './Chart.css'

echarts.use([
  BarChart,
  LineChart,
  /* The outcome ring at the head of the programmes card. GaugeChart used to sit
     here for the dials it replaced; nothing draws a gauge any more. */
  PieChart,
  /* The platform-KPI block adds two shapes: the participation funnel, and the
     organisation-average benchmark line drawn over each ranking. */
  FunnelChart,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  TooltipComponent,
  CanvasRenderer,
  /* FOR PRINT. A canvas goes into a PDF as a bitmap at whatever pixel density the page was
     drawn at, which is what makes the legacy's exported charts soft; an SVG chart is drawn by
     the printer at the printer's resolution and its labels stay real, selectable text. Both
     renderers ship with echarts, so this costs the bundle a renderer and no dependency. */
  SVGRenderer,
])

/**
 * Apache ECharts wrapper. Disposes on unmount and re-fits on container resize,
 * which a fixed `height` alone does not handle inside a flex/grid page.
 *
 * `option` is merged over the shared defaults in `chartTheme.js`, so every chart in
 * the panel inherits the same type scale, gridlines and tooltip behaviour.
 *
 * @param {object} option  an ECharts option object
 * @param {number} height  chart height in px
 * @param {'canvas'|'svg'} renderer  `svg` for anything that will be printed
 */
export default function Chart({ option, height = 300, className = '', ariaLabel, renderer = 'canvas' }) {
  const hostRef = useRef(null)
  const chartRef = useRef(null)

  useEffect(() => {
    const chart = echarts.init(hostRef.current, null, { renderer })
    chartRef.current = chart

    const observer = new ResizeObserver(() => chart.resize())
    observer.observe(hostRef.current)

    return () => {
      observer.disconnect()
      chart.dispose()
      chartRef.current = null
    }
  }, [renderer])

  useEffect(() => {
    // `true` replaces rather than merges, so removing a series actually removes it.
    chartRef.current?.setOption(option, true)
  }, [option])

  return (
    <div
      ref={hostRef}
      className={`chart ${className}`.trim()}
      style={{ height }}
      role="img"
      aria-label={ariaLabel}
      /* The legacy charts are all dir="ltr"; axes read left-to-right even in Arabic. */
      dir="ltr"
    />
  )
}
